import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { Box, Button, FormControlLabel, MenuItem, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { useGetLensCatalogue } from '~/hooks/apis/reporting/useLensCatalogue';
import {
  type LensCheck,
  type LensFieldDataset,
  type LensFieldMeasure,
  useCheckLensOperations,
  useGetReportingLensFields,
} from '~/hooks/apis/reporting/useLensAuthoring';

import { useDebouncedValue } from '../useDebouncedValue';

import { WhereAdder } from './QuerySettings';
import RawSettings, { replaceDefinition } from './RawSettings';
import type { BoardOperation } from './editor';
import { conditionText } from './format';
import { NAMED_EMPTY, Reveal } from './settingsKit';
import type { Condition, LensDocumentData } from './types';
import type { BoardEditor } from './useBoardEdit';

type DataPanelProps = { lensId: string; lensVersion: number; editor: BoardEditor; data?: LensDocumentData };

const KIND_WORDS: Record<LensFieldDataset['kind'], string> = { catalogue: 'Governed dataset', sql: 'SQL written for this Lens', table: 'A table kept in this Lens' };

const FORMATS: Array<[string, string]> = [
  ['aud', 'A$'],
  ['usd', 'US$'],
  ['int', 'Whole number'],
  ['num1', '1 decimal'],
  ['num2', '2 decimals'],
  ['pct', '%'],
  ['days', 'Days'],
  ['nights', 'Nights'],
];

const AGGREGATES: Array<[string, string, (column: string) => string]> = [
  ['sum', 'Sum', (column) => `sum(${column})`],
  ['avg', 'Average', (column) => `avg(${column})`],
  ['count', 'Count rows', () => 'count(*)'],
  ['distinct', 'Count distinct', (column) => `count(distinct ${column})`],
  ['min', 'Lowest', (column) => `min(${column})`],
  ['max', 'Highest', (column) => `max(${column})`],
  ['median', 'Median', (column) => `median(${column})`],
  ['p90', '90th percentile', (column) => `percentile(${column}, 90)`],
];

const ROLLUPS: Array<[string, string]> = [
  ['', 'Worked out from its formula'],
  ['sum', 'Yes: parts sum to the total'],
  ['none', 'No: a rate, average or count of distinct'],
];

const DECIMALS: Array<[string, string]> = [
  ['', 'Auto'],
  ['0', '0'],
  ['1', '1'],
  ['2', '2'],
  ['3', '3'],
];

const ROW_GRAINS: Array<[string, string]> = [
  ['', 'Any'],
  ['day', 'Day'],
  ['week', 'Week'],
  ['month', 'Month'],
  ['quarter', 'Quarter'],
  ['year', 'Year'],
];

const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 };

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <Typography color="text.secondary" sx={{ borderTop: 1, borderColor: 'divider', fontWeight: 600, letterSpacing: 0.4, pt: 1.5, textTransform: 'uppercase' }} variant="caption">
      {children}
    </Typography>
  );
}

function Problems({ check }: { check?: LensCheck }) {
  if (!check?.problems.length) return null;
  return (
    <Stack component="ul" spacing={0.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {check.problems.map((problem) => (
        <Typography color={problem.severity === 'warning' ? 'text.secondary' : 'error'} component="li" key={`${problem.path}:${problem.code}`} variant="caption">
          {problem.message}
          {problem.remedy ? ` ${problem.remedy}` : ''}
        </Typography>
      ))}
    </Stack>
  );
}

function Preview({ preview }: { preview?: LensCheck['preview'] }) {
  if (!preview) return null;
  if (preview.error)
    return (
      <Typography color="error" variant="caption">
        {preview.error}
      </Typography>
    );
  return (
    <Box sx={{ maxHeight: 240, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: '8px' }}>
      <Box component="table" sx={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', '& th, & td': { borderBottom: 1, borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left', whiteSpace: 'nowrap' } }}>
        <thead>
          <tr>
            {preview.columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preview.rows.map((row, index) => (
            <tr key={index}>
              {preview.columns.map((column) => (
                <td key={column}>{row[column] === null || row[column] === undefined ? '' : String(typeof row[column] === 'object' ? JSON.stringify(row[column]) : row[column])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function SqlEditor({ dataset, lensId, editor }: { dataset: LensFieldDataset; lensId: string; editor: BoardEditor }) {
  const [sql, setSql] = useState(dataset.sql ?? '');
  const check = useCheckLensOperations(lensId);
  useEffect(() => setSql(dataset.sql ?? ''), [dataset.sql]);
  const operation: BoardOperation = { op: 'dataset', id: dataset.id, def: { sql } };
  return (
    <Stack spacing={1}>
      <TextField fullWidth inputProps={{ 'aria-label': `SQL of ${dataset.label}`, spellCheck: false, style: MONO }} minRows={6} multiline onChange={(event) => setSql(event.target.value)} size="small" value={sql} />
      <Stack direction="row" spacing={1}>
        <Button disabled={check.isPending || !sql.trim()} onClick={() => check.mutate({ operations: [operation], preview: dataset.id })} size="small" variant="text">
          {check.isPending ? 'Running…' : 'Run preview'}
        </Button>
        <Button disabled={editor.pending || sql === dataset.sql || !sql.trim()} onClick={() => editor.apply(operation)} size="small" variant="contained">
          Save
        </Button>
      </Stack>
      <Problems check={check.data} />
      <Preview preview={check.data?.preview} />
    </Stack>
  );
}

function pastedTable(text: string): { columns: Array<{ name: string; type: string }>; rows: Array<Array<unknown>> } | undefined {
  const lines = text.trim().split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return undefined;
  const separator = lines[0].includes('\t') ? '\t' : ',';
  const cells = lines.map((line) => line.split(separator).map((cell) => cell.trim()));
  const names = cells[0].map((name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'column');
  const body = cells.slice(1);
  const typeOf = (index: number) => {
    const values = body.map((row) => row[index]).filter(Boolean);
    if (values.every((value) => /^-?[\d,]*\.?\d+$/.test(value))) return 'NUMBER';
    if (values.every((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))) return 'DATE';
    if (values.every((value) => /^(true|false)$/i.test(value))) return 'BOOL';
    return 'STRING';
  };
  const columns = names.map((name, index) => ({ name, type: typeOf(index) }));
  const rows = body.map((row) =>
    columns.map((column, index) => {
      const value = row[index] ?? '';
      if (!value) return null;
      if (column.type === 'NUMBER') return Number(value.replace(/,/g, ''));
      if (column.type === 'BOOL') return /^true$/i.test(value);
      return value;
    }),
  );
  return { columns, rows };
}

function NewDataset({ lensId, editor, taken }: { lensId: string; editor: BoardEditor; taken: Set<string> }) {
  const [kind, setKind] = useState<'catalogue' | 'sql' | 'table'>('catalogue');
  const [id, setId] = useState('');
  const [catalogue, setCatalogue] = useState('');
  const [sql, setSql] = useState('');
  const [pasted, setPasted] = useState('');
  const catalogueList = useGetLensCatalogue(kind === 'catalogue');
  const check = useCheckLensOperations(lensId);
  const table = kind === 'table' ? pastedTable(pasted) : undefined;
  const name = (id || catalogue).trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
  const def = kind === 'catalogue' ? { catalogue } : kind === 'sql' ? { sql } : table;
  const ready = Boolean(name) && !taken.has(name) && Boolean(kind === 'catalogue' ? catalogue : kind === 'sql' ? sql.trim() : table);
  const operation = (): BoardOperation => ({ op: 'dataset', id: name, def: def as Record<string, unknown> });
  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup aria-label="New dataset from" exclusive fullWidth onChange={(_, next) => next && setKind(next)} size="small" sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }} value={kind}>
        <ToggleButton value="catalogue">Catalogue</ToggleButton>
        <ToggleButton value="sql">SQL</ToggleButton>
        <ToggleButton value="table">Paste a table</ToggleButton>
      </ToggleButtonGroup>
      {kind === 'catalogue' ? (
        <TextField disabled={catalogueList.isLoading} fullWidth label="Governed dataset" onChange={(event) => setCatalogue(event.target.value)} select size="small" value={catalogue}>
          {(catalogueList.data?.datasets ?? []).map((entry) => (
            <MenuItem key={entry.id} value={entry.id}>
              <Stack>
                <Typography variant="body2">{entry.name}</Typography>
                <Typography color="text.secondary" variant="caption">
                  {entry.purpose}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      <TextField
        error={taken.has(name)}
        fullWidth
        helperText={taken.has(name) ? 'This Lens already has a dataset by that name.' : 'Measures refer to it by this name.'}
        InputLabelProps={{ shrink: true }}
        label="Name"
        onChange={(event) => setId(event.target.value)}
        placeholder={catalogue || 'bookings'}
        size="small"
        value={id}
      />
      {kind === 'sql' ? <TextField fullWidth inputProps={{ 'aria-label': 'SQL of the new dataset', spellCheck: false, style: MONO }} minRows={6} multiline onChange={(event) => setSql(event.target.value)} placeholder="SELECT … FROM … WHERE date BETWEEN @start_date AND @end_date" size="small" value={sql} /> : null}
      {kind === 'table' ? (
        <TextField
          fullWidth
          helperText={table ? `${table.columns.length} columns (${table.columns.map((column) => `${column.name} ${column.type.toLowerCase()}`).join(', ')}), ${table.rows.length} rows` : 'Paste rows copied from a spreadsheet, with a header row.'}
          inputProps={{ 'aria-label': 'Pasted table', style: MONO }}
          minRows={5}
          multiline
          onChange={(event) => setPasted(event.target.value)}
          size="small"
          value={pasted}
        />
      ) : null}
      <Stack direction="row" spacing={1}>
        {kind === 'sql' ? (
          <Button disabled={!ready || check.isPending} onClick={() => check.mutate({ operations: [operation()], preview: name })} size="small" variant="text">
            {check.isPending ? 'Running…' : 'Run preview'}
          </Button>
        ) : null}
        <Button
          disabled={!ready || editor.pending}
          onClick={() => {
            editor.apply(operation());
            setId('');
            setSql('');
            setPasted('');
          }}
          size="small"
          startIcon={<AddIcon />}
          variant="contained"
        >
          Add dataset
        </Button>
      </Stack>
      <Problems check={check.data} />
      <Preview preview={check.data?.preview} />
    </Stack>
  );
}

function ColumnLabel({ dataset, name, label, editor }: { dataset: LensFieldDataset; name: string; label: string; editor: BoardEditor }) {
  const [draft, setDraft] = useState(dataset.labels?.[name] ?? '');
  useEffect(() => setDraft(dataset.labels?.[name] ?? ''), [dataset.labels, name]);
  const commit = () => {
    const next = draft.trim();
    if (next === (dataset.labels?.[name] ?? '')) return;
    const labels = Object.fromEntries(Object.entries({ ...dataset.labels, [name]: next }).filter(([, value]) => value));
    editor.apply({ op: 'dataset', id: dataset.id, def: { labels: Object.keys(labels).length ? labels : null } });
  };
  return (
    <TextField
      InputLabelProps={{ shrink: true }}
      label={name}
      onBlur={commit}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => event.key === 'Enter' && (event.target as HTMLInputElement).blur()}
      placeholder={label}
      size="small"
      value={draft}
    />
  );
}

function CalculatedColumns({ dataset, editor }: { dataset: LensFieldDataset; editor: BoardEditor }) {
  const [name, setName] = useState('');
  const [sql, setSql] = useState('');
  const derived = dataset.derived ?? {};
  const id = name.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
  const save = (next: Record<string, string>) => editor.apply({ op: 'dataset', id: dataset.id, def: { derived: Object.keys(next).length ? next : null } });
  return (
    <Stack spacing={1}>
      <Typography color="text.secondary" variant="caption">
        Calculated columns, worked out on each row before grouping
      </Typography>
      {Object.entries(derived).map(([column, expression]) => (
        <Stack alignItems="flex-start" direction="row" key={column} spacing={1}>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={MONO}>{column}</Typography>
            <Typography color="text.secondary" sx={{ ...MONO, overflowWrap: 'anywhere' }}>
              {expression}
            </Typography>
          </Stack>
          <Button
            color="error"
            disabled={editor.pending}
            onClick={() => save(Object.fromEntries(Object.entries(derived).filter(([other]) => other !== column)))}
            size="small"
            variant="text"
          >
            Remove
          </Button>
        </Stack>
      ))}
      <TextField InputLabelProps={{ shrink: true }} label="New column" onChange={(event) => setName(event.target.value)} placeholder="lead_band" size="small" value={name} />
      <TextField
        helperText="SQL over this dataset’s columns, e.g. CASE WHEN lead_days < 30 THEN 'Under 30 days' ELSE '30 days or more' END"
        inputProps={{ spellCheck: false, style: MONO }}
        label="Worked out as"
        multiline
        onChange={(event) => setSql(event.target.value)}
        size="small"
        value={sql}
      />
      <Box>
        <Button
          disabled={editor.pending || !id || !sql.trim() || dataset.columns.some((column) => column.name === id && !derived[id])}
          onClick={() => {
            save({ ...derived, [id]: sql.trim() });
            setName('');
            setSql('');
          }}
          size="small"
          startIcon={<AddIcon />}
          variant="text"
        >
          Add column
        </Button>
      </Box>
    </Stack>
  );
}

function DatasetDetails({ dataset, measures, data, editor }: { dataset: LensFieldDataset; measures: Array<LensFieldMeasure>; data?: LensDocumentData; editor: BoardEditor }) {
  const [removing, setRemoving] = useState(false);
  const readers = dataset.usedBy ?? [];
  const own = measures.filter((measure) => measure.dataset === dataset.id && !measure.certified);
  const dates = dataset.columns.filter((column) => column.kind === 'date');
  const named = (id: string) => data?.blocks[id]?.name ?? id;
  const remove = () =>
    editor.apply([
      ...readers.map((id): BoardOperation => ({ op: 'remove', id })),
      ...own.map((measure): BoardOperation => ({ op: 'measure', id: measure.id, def: null })),
      { op: 'dataset', id: dataset.id, def: null },
    ]);
  return (
    <Stack spacing={1.5}>
      {dataset.kind === 'sql' ? (
        <Stack direction="row" spacing={1}>
          <TextField
            {...NAMED_EMPTY}
            helperText="The Lens period and comparisons filter this column."
            label="Period column"
            onChange={(event) => editor.apply({ op: 'dataset', id: dataset.id, def: { date: event.target.value || null } })}
            select
            size="small"
            sx={{ flex: 2 }}
            value={dataset.date ?? ''}
          >
            <MenuItem value="">None: every row, whatever the period</MenuItem>
            {dates.map((column) => (
              <MenuItem key={column.name} value={column.name}>
                {column.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField defaultValue={dataset.ready ?? ''} key={dataset.ready ?? ''} label="Lands by" onBlur={(event) => event.target.value !== (dataset.ready ?? '') && editor.apply({ op: 'dataset', id: dataset.id, def: { ready: event.target.value || null } })} placeholder="09:00" size="small" sx={{ flex: 1 }} />
          <TextField {...NAMED_EMPTY} label="One row per" onChange={(event) => editor.apply({ op: 'dataset', id: dataset.id, def: { dateGrain: event.target.value || null } })} select size="small" sx={{ flex: 1 }} value={dataset.dateGrain ?? ''}>
            {ROW_GRAINS.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      ) : null}
      {dataset.kind === 'sql' ? <CalculatedColumns dataset={dataset} editor={editor} /> : null}
      <Typography color="text.secondary" variant="caption">
        {dataset.kind === 'sql' ? 'Column names as readers see them' : 'Columns'}
      </Typography>
      {dataset.kind === 'sql' ? (
        <Stack spacing={1}>
          {dataset.columns.map((column) => (
            <ColumnLabel dataset={dataset} editor={editor} key={column.name} label={column.label} name={column.name} />
          ))}
        </Stack>
      ) : (
        <Typography color="text.secondary" variant="caption">
          {dataset.columns.map((column) => `${column.label} (${column.kind})`).join(' · ')}
        </Typography>
      )}
      <Typography color="text.secondary" variant="caption">
        {readers.length ? `Read by ${readers.map(named).join(', ')}.` : 'No block reads it yet.'}
      </Typography>
      {removing ? (
        <Stack spacing={1} sx={{ bgcolor: 'action.hover', borderRadius: '8px', p: 1.5 }}>
          <Typography variant="body2">
            {readers.length || own.length
              ? `Removing ${dataset.label} also removes ${[readers.length ? `${readers.length} block${readers.length > 1 ? 's' : ''}` : '', own.length ? `${own.length} measure${own.length > 1 ? 's' : ''}` : ''].filter(Boolean).join(' and ')} that read it. Undo brings them back.`
              : `Remove ${dataset.label}? Undo brings it back.`}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button color="error" disabled={editor.pending} onClick={remove} size="small" variant="contained">
              Remove
            </Button>
            <Button onClick={() => setRemoving(false)} size="small" variant="text">
              Keep it
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Box>
          <Button color="error" disabled={editor.pending} onClick={() => setRemoving(true)} size="small" variant="text">
            Remove dataset
          </Button>
        </Box>
      )}
    </Stack>
  );
}

type MeasureDraft = { label: string; format: string; expr: string; lowerIsBetter: boolean; definition: string; decimals: string; where: Record<string, Condition>; from: string; internal: boolean; rollup: string; minOf: string; minN: string };

const draftOf = (measure?: LensFieldMeasure): MeasureDraft => ({
  label: measure?.label ?? '',
  format: measure?.format ?? '',
  expr: measure?.expr ?? measure?.column ?? '',
  lowerIsBetter: Boolean(measure?.lowerIsBetter),
  definition: measure?.definition ?? '',
  decimals: typeof measure?.decimals === 'number' ? String(measure.decimals) : '',
  where: measure?.where ?? {},
  from: measure?.dataset ?? '',
  internal: Boolean(measure?.internal),
  rollup: measure?.rollup ?? '',
  minOf: measure?.minN?.[0] ?? '',
  minN: measure?.minN ? String(measure.minN[1]) : '',
});

function MeasureForm({ lensId, editor, measure, taken, datasets, options, onDone }: { lensId: string; editor: BoardEditor; measure?: LensFieldMeasure; taken: Set<string>; datasets: Array<LensFieldDataset>; options?: Record<string, Array<string>>; onDone: () => void }) {
  const [id, setId] = useState('');
  const [draft, setDraft] = useState<MeasureDraft>(() => draftOf(measure));
  const check = useCheckLensOperations(lensId);
  const name = measure?.id ?? id.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
  const def = {
    label: draft.label.trim() || null,
    format: draft.format || null,
    ...(measure?.column && draft.expr === measure.column ? {} : { expr: draft.expr.trim() || null, column: null }),
    lowerIsBetter: draft.lowerIsBetter || null,
    definition: draft.definition.trim() || null,
    decimals: draft.decimals === '' ? null : Number(draft.decimals),
    where: Object.keys(draft.where).length ? { ...Object.fromEntries(Object.keys(measure?.where ?? {}).map((column) => [column, null])), ...draft.where } : null,
    internal: draft.internal || null,
    rollup: draft.rollup || null,
    minN: draft.minOf && Number(draft.minN) > 0 ? [draft.minOf, Number(draft.minN)] : null,
    ...(draft.from ? { from: draft.from } : {}),
  };
  const [source, setSource] = useState('');
  const [aggregate, setAggregate] = useState('sum');
  const build = (column: string, how: string) => {
    const formula = AGGREGATES.find(([id]) => id === how)?.[2];
    if (!column || !formula) return;
    set({ expr: formula(column), from: column.slice(0, column.indexOf('.')) });
  };
  const sourceColumns = datasets.flatMap((dataset) => dataset.columns.map((column): [string, string] => [`${dataset.id}.${column.name}`, `${column.label} · ${dataset.label}`]));
  const conditionColumns = datasets.flatMap((dataset) => dataset.columns.filter((column) => column.kind === 'dimension').map((column): [string, string] => [column.name, column.label]));
  const numberColumns = datasets.flatMap((dataset) => dataset.columns.filter((column) => column.kind === 'measure').map((column): [string, string] => [column.name, column.label]));
  const settled = useDebouncedValue(JSON.stringify([name, def]), 600);
  const { mutate } = check;
  useEffect(() => {
    if (name && (draft.expr.trim() || measure)) mutate({ operations: [{ op: 'measure', id: name, def }] });
  }, [settled]);
  const set = (patch: Partial<MeasureDraft>) => setDraft({ ...draft, ...patch });
  const ready = Boolean(name) && (Boolean(measure) || !taken.has(name)) && Boolean(draft.expr.trim()) && !check.data?.refused;
  return (
    <Stack spacing={1.25} sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5 }}>
      {measure ? null : <TextField error={taken.has(name)} fullWidth helperText={taken.has(name) ? 'That name is taken.' : undefined} label="Name" onChange={(event) => setId(event.target.value)} placeholder="margin_per_order" size="small" value={id} InputLabelProps={{ shrink: true }} />}
      <TextField fullWidth label="Label" onChange={(event) => set({ label: event.target.value })} size="small" value={draft.label} />
      <Stack direction="row" spacing={1}>
        <TextField {...NAMED_EMPTY} label="From the column" onChange={(event) => { setSource(event.target.value); build(event.target.value, aggregate); }} select size="small" sx={{ flex: 2 }} value={source}>
          <MenuItem value="">Custom expression</MenuItem>
          {sourceColumns.map(([id, label]) => (
            <MenuItem key={id} value={id}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField label="As" onChange={(event) => { setAggregate(event.target.value); build(source, event.target.value); }} select size="small" sx={{ flex: 1 }} value={aggregate}>
          {AGGREGATES.map(([id, label]) => (
            <MenuItem key={id} value={id}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <TextField fullWidth inputProps={{ spellCheck: false, style: MONO }} label="Expression" helperText="SQL over the dataset’s columns, or {measure} references, e.g. {margin} / {orders}" multiline onChange={(event) => set({ expr: event.target.value })} size="small" value={draft.expr} />
      <Stack direction="row" spacing={1}>
        <TextField {...NAMED_EMPTY} label="Format" onChange={(event) => set({ format: event.target.value })} select size="small" sx={{ minWidth: 140 }} value={draft.format}>
          <MenuItem value="">Auto</MenuItem>
          {FORMATS.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField {...NAMED_EMPTY} label="Decimals" onChange={(event) => set({ decimals: event.target.value })} select size="small" sx={{ minWidth: 96 }} value={draft.decimals}>
          {DECIMALS.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <FormControlLabel control={<Switch checked={draft.lowerIsBetter} onChange={(event) => set({ lowerIsBetter: event.target.checked })} />} label="Lower is better (a fall shows green)" />
      <FormControlLabel control={<Switch checked={draft.internal} onChange={(event) => set({ internal: event.target.checked })} />} label="Hidden from partners" />
      <TextField {...NAMED_EMPTY} label="Adds up across rows" onChange={(event) => set({ rollup: event.target.value })} select size="small" value={draft.rollup}>
        {ROLLUPS.map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction="row" spacing={1}>
        <TextField InputLabelProps={{ shrink: true }} label="Needs a sample of at least" onChange={(event) => set({ minN: event.target.value })} placeholder="None" size="small" sx={{ flex: 1 }} type="number" value={draft.minN} />
        <TextField {...NAMED_EMPTY} label="Counted by" onChange={(event) => set({ minOf: event.target.value })} select size="small" sx={{ flex: 1 }} value={draft.minOf}>
          <MenuItem value="">No minimum</MenuItem>
          {[...taken].map((id) => (
            <MenuItem key={id} value={id}>
              {id}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Box>
        <Typography color="text.secondary" variant="caption">
          Counts only rows where
        </Typography>
        {Object.entries(draft.where).map(([column, condition]) => (
          <Stack alignItems="center" direction="row" key={column} spacing={0.5}>
            <Typography sx={{ flex: 1 }} variant="body2">{conditionText([...conditionColumns, ...numberColumns].find(([name]) => name === column)?.[1] ?? column, condition)}</Typography>
            <Button onClick={() => set({ where: Object.fromEntries(Object.entries(draft.where).filter(([name]) => name !== column)) })} size="small" variant="text">
              Remove
            </Button>
          </Stack>
        ))}
        <Reveal label="Add a condition">
          {(close) => (
            <WhereAdder
              columns={conditionColumns}
              disabled={false}
              numbers={numberColumns}
              onAdd={(column, condition) => {
                set({ where: { ...draft.where, [column]: condition } });
                close();
              }}
              options={options}
            />
          )}
        </Reveal>
      </Box>
      <TextField fullWidth label="Definition" minRows={2} multiline onChange={(event) => set({ definition: event.target.value })} size="small" value={draft.definition} />
      <Typography color={check.data?.refused ? 'error' : 'text.secondary'} variant="caption">
        {check.isPending ? 'Checking…' : check.data ? (check.data.refused ? '' : 'Checks out.') : ''}
      </Typography>
      <Problems check={check.data} />
      {measure ? (
        <RawSettings
          disabled={editor.pending}
          onSet={(key, value) => editor.apply(replaceDefinition('measure', measure.id, key, value))}
          skip={['id', 'certified', 'dataset', 'problem']}
          title="Every setting"
          values={measure as unknown as Record<string, unknown>}
        />
      ) : null}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={onDone} size="small" variant="text">
          Cancel
        </Button>
        <Button
          disabled={!ready || editor.pending}
          onClick={() => {
            editor.apply({ op: 'measure', id: name, def });
            onDone();
          }}
          size="small"
          variant="contained"
        >
          {measure ? 'Save measure' : 'Add measure'}
        </Button>
      </Stack>
    </Stack>
  );
}

export default function DataPanel({ lensId, lensVersion, editor, data }: DataPanelProps) {
  const fields = useGetReportingLensFields(lensId, lensVersion);
  const [open, setOpen] = useState<string>();
  const [adding, setAdding] = useState<'dataset' | 'measure'>();
  const [editing, setEditing] = useState<string>();
  const datasets = fields.data?.datasets ?? [];
  const measures = fields.data?.measures ?? [];
  const certified = datasets.flatMap((dataset) => dataset.certified.map((measure) => ({ ...measure, dataset: dataset.label })));

  return (
    <Stack data-lens-data-panel spacing={2} sx={{ p: 2 }}>
      {fields.isLoading ? <Box aria-busy sx={{ bgcolor: 'action.hover', borderRadius: '8px', height: 120 }} /> : null}
      {fields.error ? (
        <Typography color="error" role="alert" variant="body2">
          {fields.error.message || 'The Lens’s data couldn’t be read.'}
        </Typography>
      ) : null}
      <Heading>Datasets</Heading>
      {datasets.map((dataset) => (
        <Stack key={dataset.id} spacing={1}>
          <Button
            aria-expanded={open === dataset.id}
            onClick={() => setOpen(open === dataset.id ? undefined : dataset.id)}
            size="small"
            sx={{ color: 'text.primary', justifyContent: 'space-between', textAlign: 'left', textTransform: 'none' }}
            variant="text"
          >
            <Stack>
              <Typography variant="body2">{dataset.label}</Typography>
              <Typography color="text.secondary" variant="caption">
                {`${KIND_WORDS[dataset.kind]} · ${dataset.columns.length} fields`}
              </Typography>
            </Stack>
          </Button>
          {dataset.problem ? (
            <Typography color="error" variant="caption">
              {dataset.problem}
            </Typography>
          ) : null}
          {open === dataset.id ? (
            <Stack spacing={1.5} sx={{ pl: 1 }}>
              {dataset.kind === 'sql' ? <SqlEditor dataset={dataset} editor={editor} lensId={lensId} /> : null}
              <DatasetDetails data={data} dataset={dataset} editor={editor} measures={measures} />
            </Stack>
          ) : null}
        </Stack>
      ))}
      {adding === 'dataset' ? (
        <NewDataset editor={editor} lensId={lensId} taken={new Set(datasets.map((dataset) => dataset.id))} />
      ) : (
        <Box>
          <Button onClick={() => setAdding('dataset')} size="small" startIcon={<AddIcon />} variant="text">
            New dataset
          </Button>
        </Box>
      )}
      <Heading>Measures</Heading>
      {measures.map((measure) =>
        editing === measure.id ? (
          <MeasureForm datasets={datasets} editor={editor} key={measure.id} lensId={lensId} measure={measure} onDone={() => setEditing(undefined)} options={data?.options} taken={new Set()} />
        ) : (
          <Button key={measure.id} onClick={() => setEditing(measure.id)} size="small" sx={{ color: 'text.primary', justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none' }} variant="text">
            <Stack>
              <Typography variant="body2">{measure.label}</Typography>
              <Typography color={measure.problem ? 'error' : 'text.secondary'} sx={measure.problem ? undefined : MONO} variant="caption">
                {measure.problem ?? measure.expr ?? measure.column ?? measure.id}
              </Typography>
            </Stack>
          </Button>
        ),
      )}
      {adding === 'measure' ? (
        <MeasureForm datasets={datasets} editor={editor} lensId={lensId} onDone={() => setAdding(undefined)} options={data?.options} taken={new Set([...measures.map((measure) => measure.id), ...certified.map((measure) => measure.id)])} />
      ) : (
        <Box>
          <Button onClick={() => setAdding('measure')} size="small" startIcon={<AddIcon />} variant="text">
            Add a measure
          </Button>
        </Box>
      )}
      {certified.length ? (
        <>
          <Heading>Certified measures</Heading>
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {certified.map((measure) => (
              <Box component="li" key={`${measure.dataset}:${measure.id}`}>
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  <Typography variant="body2">{measure.label}</Typography>
                  <VerifiedOutlinedIcon aria-label="Certified" color="primary" sx={{ fontSize: 14 }} />
                </Stack>
                <Typography color="text.secondary" variant="caption">
                  {measure.definition ?? measure.dataset}
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      ) : null}
    </Stack>
  );
}
